import { useEffect, useMemo, useRef, type FunctionComponent } from "react";
import { CheckOutlined, CloseOutlined, ExportOutlined } from "@ant-design/icons";
import { Button, Space, Table, Tag, Typography, type TableProps } from "antd";
import isEqual from "lodash.isequal";
import truncate from "lodash.truncate";
import uniq from "lodash.uniq";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { RequestStatus, traineesActions, userSelectors } from "@fitness-app/app-store";
import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { type AppAccessType } from "@fitness-app/data-models/entities/Automation";
import { type TeamMemberWithUser } from "@fitness-app/data-models/entities/TeamMember";
import {
  type TraineeStatus,
  type TraineeWithAssignedTrainerAndProduct,
} from "@fitness-app/data-models/entities/Trainee";
import { getAuthorName } from "@fitness-app/data-models/utils/getAuthorName";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { usePermission } from "~/hooks/usePermission";
import { useTagOptions } from "~/hooks/useTagOptions";
import { createClientsAccess } from "~/modules/Trainee/clientsAccess";
import TraineeStatusBadge from "~/modules/Trainee/components/TraineeStatusBadge/TraineeStatusBadge";
import {
  AppAccessTypeBadge,
  createAppAccessTypeOptions,
  createStatusOptions,
} from "~/modules/Trainee/helpers/createStatusOptions";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { createTrainerOptions } from "./helpers/createTrainerOptions";

interface OwnProps {
  isSearchMode: boolean;
  trainers: TeamMemberWithUser[];
}

type Props = OwnProps;

const TraineesTable: FunctionComponent<Props> = ({ isSearchMode, trainers }) => {
  const { t } = useTranslation("trainees");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { memberId } = useUserClaims();
  const permissions = useAppSelector(userSelectors.getUserPermissions);
  const access = createClientsAccess(permissions);
  // Wejście w kartę produktu klientki — ta sama bramka co reszta ekranów
  // produktowych, wcześniej `isOwner`.
  const canOpenProductScreens = usePermission(PermissionKey.PRODUCTS_MANAGE, PermissionScope.ALL);
  const { filters: traineeTags } = useTagOptions("traineeTags");

  // Helper function to normalize filter values for comparison
  // Treats null, undefined, and empty arrays as equivalent "no filter" state
  const normalizeFilterValue = (value: any[] | null | undefined): any[] | null => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return null;
    }
    return value;
  };

  const {
    listStatus,
    list,
    page,
    listSize,
    totalPages,
    filters: {
      currentAssignedTrainerFilter,
      currentStatusFilter,
      currentAccessType,
      currentFilteredTags,
      currentGhostFilter,
    },
  } = useAppSelector((store) => store.trainees);
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialRun = useRef(listStatus !== RequestStatus.SUCCESS);

  useEffect(() => {
    const page = searchParams.get("page");

    void dispatch(
      traineesActions.fetchTrainees(
        // Zakres ASSIGNED zawsze schodzi do własnych podopiecznych; TEAM tylko
        // na pierwszym wejściu, dalej decyduje filtr z UI.
        memberId && (access.pinnedToOwnClients || (access.canToggleAssignedTrainers && isInitialRun.current))
          ? {
              assignedTrainerId: memberId,
              page: page ? Number(page) : undefined,
            }
          : { page: page ? Number(page) : undefined },
      ),
    );
  }, [dispatch, memberId]);

  const handleChange: TableProps<TraineeWithAssignedTrainerAndProduct>["onChange"] = (pagination, filters) => {
    if (access.pinnedToOwnClients) {
      if (
        !isEqual(
          normalizeFilterValue(currentStatusFilter),
          normalizeFilterValue(filters.status as TraineeStatus[] | null),
        )
      ) {
        const newStatusFilter = filters.status as TraineeStatus[] | null;
        setSearchParams({ page: String(1) });

        void dispatch(
          traineesActions.fetchTrainees({
            statuses: newStatusFilter?.length ? newStatusFilter : null,
            assignedTrainerId: memberId,
            page: 1,
          }),
        );
      } else if (!isEqual(normalizeFilterValue(currentFilteredTags), normalizeFilterValue(filters.tags))) {
        setSearchParams({ page: String(1) });

        void dispatch(
          traineesActions.fetchTrainees({
            assignedTrainerId: memberId,
            tags: filters.tags as string[],
            page: 1,
          }),
        );
      } else if (
        !isEqual(
          normalizeFilterValue(typeof currentGhostFilter === "boolean" ? [currentGhostFilter] : null),
          normalizeFilterValue(filters.ghost),
        )
      ) {
        const newGhostFilter = filters.ghost?.[0] ?? null;
        setSearchParams({ page: String(1) });
        void dispatch(
          traineesActions.fetchTrainees({
            ghost: typeof newGhostFilter === "boolean" ? Boolean(newGhostFilter) : null,
            assignedTrainerId: memberId,
            page: 1,
          }),
        );
      } else {
        if (pagination.current && pagination.current !== page) {
          setSearchParams({ page: String(pagination.current || 1) });
          void dispatch(
            traineesActions.fetchTrainees(
              memberId ? { assignedTrainerId: memberId, page: pagination.current } : { page: pagination.current },
            ),
          );
        }
      }
    } else {
      // Surowe isEqual rozjeżdżało się na null vs undefined vs [] — antd nie daje
      // klucza dla kolumny bez `filters` (np. pusta lista trenerek u sprzedawcy),
      // a wtedy KAŻDY klik paginacji wpadał w gałąź "zmiana filtra" z page: 1.
      if (
        !isEqual(
          normalizeFilterValue(currentAssignedTrainerFilter ? [currentAssignedTrainerFilter] : null),
          normalizeFilterValue(filters.assignedTrainer),
        )
      ) {
        const newTrainerFilter = filters.assignedTrainer?.[0] || null;
        setSearchParams({ page: String(1) });
        void dispatch(
          traineesActions.fetchTrainees({
            statuses: currentStatusFilter,
            assignedTrainerId: (newTrainerFilter as string) || null,
            page: 1,
          }),
        );
      } else if (
        !isEqual(
          normalizeFilterValue(currentStatusFilter),
          normalizeFilterValue(filters.status as TraineeStatus[] | null),
        )
      ) {
        const newStatusFilter = filters.status as TraineeStatus[] | null;
        setSearchParams({ page: String(1) });
        void dispatch(
          traineesActions.fetchTrainees({
            statuses: newStatusFilter?.length ? newStatusFilter : null,
            assignedTrainerId: currentAssignedTrainerFilter || null,
            page: 1,
          }),
        );
      } else if (!isEqual(normalizeFilterValue(currentFilteredTags), normalizeFilterValue(filters.tags))) {
        setSearchParams({ page: String(1) });
        void dispatch(
          traineesActions.fetchTrainees({
            tags: filters.tags as string[],
            assignedTrainerId: currentAssignedTrainerFilter || null,
            page: 1,
          }),
        );
      } else if (
        !isEqual(
          normalizeFilterValue(typeof currentGhostFilter === "boolean" ? [currentGhostFilter] : null),
          normalizeFilterValue(filters.ghost),
        )
      ) {
        const newGhostFilter = filters.ghost?.[0] ?? null;
        setSearchParams({ page: String(1) });
        void dispatch(
          traineesActions.fetchTrainees({
            ghost: typeof newGhostFilter === "boolean" ? Boolean(newGhostFilter) : null,
            assignedTrainerId: currentAssignedTrainerFilter || null,
            page: 1,
          }),
        );
      } else if (!isEqual(normalizeFilterValue(currentAccessType), normalizeFilterValue(filters.type))) {
        const newAccessType = filters.type as AppAccessType[] | null;
        setSearchParams({ page: String(1) });
        void dispatch(
          traineesActions.fetchTrainees({
            accessType: newAccessType?.length ? newAccessType : null,
            assignedTrainerId: currentAssignedTrainerFilter || null,
            page: 1,
          }),
        );
      } else {
        if (pagination.current && pagination.current !== page) {
          setSearchParams({ page: String(pagination.current || 1) });
          void dispatch(traineesActions.fetchTrainees({ page: pagination.current }));
        }
      }
    }
  };

  const tags = useMemo(() => {
    return [
      ...traineeTags,
      ...traineeTags.map((tag) => ({
        text: `Bez tagu: ${tag.text}`,
        value: `WITHOUT__${tag.value}`,
      })),
    ];
  }, [traineeTags]);

  return (
    <>
      <Table<TraineeWithAssignedTrainerAndProduct>
        dataSource={list}
        rowKey="id"
        size="middle"
        scroll={{ x: true }}
        locale={{
          emptyText:
            isSearchMode || currentAssignedTrainerFilter !== undefined || currentStatusFilter?.length
              ? t("emptyStateForFilter")
              : t("emptyState"),
        }}
        loading={listStatus === RequestStatus.FETCHING || listStatus === RequestStatus.UPDATING}
        pagination={{
          current: page,
          pageSize: listSize,
          total: totalPages * listSize,
          pageSizeOptions: ["50"],
          showSizeChanger: false,
        }}
        onChange={handleChange}
      >
        <Table.Column<TraineeWithAssignedTrainerAndProduct>
          title={t("tableHeader.index")}
          width={50}
          responsive={["md"]}
          dataIndex="index"
          key="index"
          render={(_name, _row, i) => (page > 1 ? (page - 1) * listSize + i + 1 : i + 1)}
        />
        <Table.Column<TraineeWithAssignedTrainerAndProduct>
          title={t("tableHeader.name")}
          dataIndex="name"
          key="name"
          render={(_, row) => (
            <Button type="link" className="px-0" onClick={() => navigate(`/trainee/details/${row.id}`)}>
              {row.firstName} {row.lastName}
            </Button>
          )}
        />
        <Table.Column<TraineeWithAssignedTrainerAndProduct>
          title={t("tableHeader.email")}
          dataIndex="email"
          ellipsis
          responsive={["lg"]}
          render={(email) => (
            <Typography.Link href={`mailto:${email}`} target="_blank" copyable>
              {email}
            </Typography.Link>
          )}
        />
        <Table.Column<TraineeWithAssignedTrainerAndProduct>
          title={t("tableHeader.status")}
          dataIndex="status"
          key="status"
          render={(status: TraineeStatus) => <TraineeStatusBadge status={status} />}
          filteredValue={currentStatusFilter || []}
          filters={createStatusOptions()}
        />
        <Table.Column<TraineeWithAssignedTrainerAndProduct>
          title={t("access.ghost")}
          dataIndex="ghost"
          key="ghost"
          // Kolumna z aktywnym filtrem zostaje widoczna, żeby dało się go zdjąć na telefonie.
          responsive={typeof currentGhostFilter === "boolean" ? undefined : ["lg"]}
          render={(ghost: boolean) => (ghost ? <Tag color="orange">TAK</Tag> : "-")}
          filteredValue={typeof currentGhostFilter === "boolean" ? [currentGhostFilter] : []}
          filterMultiple={false}
          filters={[
            { text: "TAK", value: true },
            { text: "NIE", value: false },
          ]}
        />
        {access.canFilterByTrainer && (
          <Table.Column<TraineeWithAssignedTrainerAndProduct>
            title={t("profileForm.accessType")}
            dataIndex="type"
            key="type"
            responsive={currentAccessType?.length ? undefined : ["xl"]}
            render={(type: AppAccessType) => <AppAccessTypeBadge type={type} />}
            filteredValue={currentAccessType}
            filters={createAppAccessTypeOptions()}
          />
        )}
        <Table.Column<TraineeWithAssignedTrainerAndProduct>
          title={t("tableHeader.workouts")}
          dataIndex="activeTrainingProgramId"
          key="activeTrainingProgramId"
          responsive={["md"]}
          render={(activeTrainingProgramId) =>
            activeTrainingProgramId ? (
              <CheckOutlined style={{ color: "#25b10f" }} />
            ) : (
              <CloseOutlined type="close" style={{ color: "#ED1C24" }} />
            )
          }
        />
        <Table.Column<TraineeWithAssignedTrainerAndProduct>
          title={t("tableHeader.diet")}
          dataIndex="activeMealsPlanId"
          key="activeMealsPlanId"
          responsive={["md"]}
          render={(activeMealsPlanId) =>
            activeMealsPlanId ? (
              <CheckOutlined style={{ color: "#25b10f" }} />
            ) : (
              <CloseOutlined style={{ color: "#ED1C24" }} />
            )
          }
        />
        <Table.Column<TraineeWithAssignedTrainerAndProduct>
          title={t("tableHeader.trainer")}
          dataIndex="assignedTrainer"
          key="assignedTrainer"
          responsive={currentAssignedTrainerFilter ? undefined : ["lg"]}
          render={(trainer: TraineeWithAssignedTrainerAndProduct["assignedTrainer"]) =>
            trainer ? <Tag color="magenta">{getAuthorName(trainer)}</Tag> : "-"
          }
          {...(!access.canFilterByTrainer
            ? {}
            : {
                filterMultiple: false,
                filterSearch: true,
                filteredValue: currentAssignedTrainerFilter ? [currentAssignedTrainerFilter] : null,
                filters: createTrainerOptions(trainers, t, false, memberId),
              })}
        />
        <Table.Column<TraineeWithAssignedTrainerAndProduct>
          title={t("tableHeader.tags")}
          dataIndex="tags"
          key="tags"
          responsive={currentFilteredTags?.length ? undefined : ["xl"]}
          filterSearch
          filterMultiple
          filteredValue={currentFilteredTags}
          filterDropdownProps={{
            overlayClassName: "w-[350px]",
          }}
          filters={tags}
          render={(tags: string[]) =>
            tags?.length ? (
              <Space direction="vertical">
                {uniq(tags).map((tag, i) => (
                  <Tag color="blue" key={`${tag}-${i}`}>
                    {tag}
                  </Tag>
                ))}
              </Space>
            ) : (
              "-"
            )
          }
        />
        {canOpenProductScreens && (
          <Table.Column<TraineeWithAssignedTrainerAndProduct>
            title={t("tableHeader.connectedProduct")}
            dataIndex="productId"
            key="productId"
            responsive={["xl"]}
            render={(_productId, row) =>
              row.productClient ? (
                <Button
                  type="link"
                  className="px-0"
                  onClick={() => navigate(`/products/${row.productClient?.productId}/clients/${row.email}`)}
                >
                  {truncate(row.productClient?.product?.name || "-", {
                    length: 20,
                  })}
                </Button>
              ) : (
                "-"
              )
            }
          />
        )}
        <Table.Column<TraineeWithAssignedTrainerAndProduct>
          title={t("tableHeader.options")}
          dataIndex="action"
          key="action"
          render={(_, row) => (
            <Space size={4}>
              <Button type="link" className="px-0" onClick={() => navigate(`/trainee/details/${row.id}`)}>
                {t(`common:details`)}
              </Button>
              <Button
                icon={<ExportOutlined />}
                title="Otwórz w nowy oknie"
                href={`${window.location.origin}/trainee/details/${row.id}`}
                target="_blank"
                type="link"
              />
            </Space>
          )}
        />
      </Table>
    </>
  );
};

export default TraineesTable;
